import { useState } from "react";
import { CheckCircle2, Send as SendIcon, X } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "../../ui/card";
import { sendInvites } from "../../../lib/copilot-api";
import type { GetProToken } from "../../../hooks/useProToken";
import type { ProPrepareInvitesRender } from "../../../lib/pro-agent-types";

// prepare_invites confirm card (scenario 684, plan 188 task 803). Lists
// EVERY selected recipient by name + email (never counts-only) — the model
// never sends (D5); Send here calls POST /api/professional/invites.
export type InvitesConfirmCardProps = {
  draft: ProPrepareInvitesRender;
  getToken: GetProToken;
  onDiscard: () => void;
};

type Status = "idle" | "sending" | "sent" | "failed";

export function InvitesConfirmCard({
  draft,
  getToken,
  onDiscard,
}: InvitesConfirmCardProps) {
  const [status, setStatus] = useState<Status>("idle");
  const [result, setResult] = useState<{ sent: number } | null>(null);

  async function handleSend() {
    setStatus("sending");
    try {
      const res = await sendInvites(
        draft.recipients.map((r) => r.contact_id),
        draft.message,
        getToken,
      );
      setResult({ sent: res.sent.length });
      setStatus("sent");
    } catch {
      setStatus("failed");
    }
  }

  if (status === "sent") {
    return (
      <Card data-testid="invites-confirm-card" data-status="sent" className="border-success/40">
        <CardContent className="flex items-center gap-2 py-3">
          <CheckCircle2 size={16} className="text-success" aria-hidden />
          <p className="text-sm font-medium text-fg">
            Invited {result?.sent ?? draft.recipients.length}{" "}
            {(result?.sent ?? draft.recipients.length) === 1
              ? "contact"
              : "contacts"}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="invites-confirm-card" data-status={status}>
      <CardHeader>
        <p className="text-sm font-medium text-fg">
          Invite {draft.recipients.length} contact
          {draft.recipients.length === 1 ? "" : "s"}
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">
          {draft.recipients.map((r) => (
            <li key={r.contact_id} className="flex items-baseline justify-between gap-2">
              <span className="truncate font-medium text-fg">{r.name}</span>
              <span className="truncate text-xs text-fg-muted">{r.email}</span>
            </li>
          ))}
        </ul>
        {draft.message && (
          <p className="whitespace-pre-wrap text-xs text-fg-muted">
            &ldquo;{draft.message}&rdquo;
          </p>
        )}
        {status === "failed" && (
          <p
            role="alert"
            data-testid="invites-failed-banner"
            className="rounded-md bg-destructive/10 px-2 py-1.5 text-xs text-destructive"
          >
            Nothing was sent — your draft is saved.
          </p>
        )}
      </CardContent>
      <CardFooter className="mt-1 gap-2 rounded-b-lg border-t border-border-soft bg-muted/40 p-4">
        <Button size="sm" onClick={handleSend} disabled={status === "sending"}>
          <SendIcon size={14} aria-hidden />
          {status === "sending"
            ? "Sending…"
            : status === "failed"
              ? "Try again"
              : "Send invites"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDiscard}>
          <X size={14} aria-hidden />
          Discard
        </Button>
      </CardFooter>
    </Card>
  );
}
